import { useState } from 'react';

import { BottomSheet, Tabs, Text, TypeSelectOption } from '@thefarmersfront/kitchen-mobile';

import { BOTTOM_SHEET_TITLES } from '~/constants';
import { useGetBankCodes } from '~/hooks';
import { TypeBankCodeState, useCodesStore } from '~/store/codes/useCodesStore';

import BankLogoImage from './BankLogoImage';
import OptionRefetchButton from './OptionRefetchButton';

import styles from './BankBottomSheet.module.scss';

const BankButton = ({
  options = [],
  onClick,
}: {
  options: TypeBankCodeState[];
  onClick: (option: TypeBankCodeState, e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const requestGetBankCodes = useGetBankCodes();

  return (
    <div className={styles['bank-button-container']}>
      {options.map((option: TypeBankCodeState) => (
        <button type="button" key={option.code} onClick={(e) => onClick(option, e)}>
          <BankLogoImage bankCode={option.code} size={26} />
          <Text weight="semi-bold" size="xlarge">
            {option.text}
          </Text>
        </button>
      ))}
      <OptionRefetchButton show={options.length === 0} onRefetch={requestGetBankCodes} />
    </div>
  );
};

/** @description 은행 또는 증권사 선택 바텀시트 컴포넌트 */
const BankBottomSheet = ({
  open,
  onClose,
  onChange,
}: {
  open: boolean;
  onClose: () => void;
  onChange: (option: TypeSelectOption, e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  type TypeSelectedTab = 'bank' | 'securities';

  const [selectedTab, setSelectedTab] = useState<TypeSelectedTab>('bank');

  const { getBankCodesByType } = useCodesStore((state) => state.actions);

  const bankCodeOptions = getBankCodesByType('BANK');
  const securityCodeOptions = getBankCodesByType('SECURITY');

  const handleChange = (option: TypeBankCodeState, e: React.MouseEvent<HTMLButtonElement>) => {
    onChange(
      {
        value: option.code,
        text: option.text,
      },
      e
    );
  };

  const menus = [
    {
      key: 'bank',
      label: '은행',
      keepMount: true,
      children: <BankButton options={bankCodeOptions} onClick={handleChange} />,
    },
    {
      key: 'securities',
      label: '증권사',
      keepMount: true,
      children: <BankButton options={securityCodeOptions} onClick={handleChange} />,
    },
  ];

  return (
    <BottomSheet
      id="bank-bottom-sheet"
      open={open ?? false}
      onClose={onClose}
      className={styles['bank-bottom-sheet']}
      showButton={false}
      title={BOTTOM_SHEET_TITLES.SELECT_BANK_NAME}
    >
      <Tabs
        className={styles['tabs']}
        menus={menus}
        activeKey={selectedTab}
        onChange={(key) => setSelectedTab(key as TypeSelectedTab)}
      />
    </BottomSheet>
  );
};

export default BankBottomSheet;
